import { Alert, Box, Button, Dialog, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import biApi from "../../../../../../../../../api/biApi";
import { createMetaDataAction } from "../../../../../store/utils/storeUtils";
import UsedByReports from "../used-by/UsedByReports";
import DeleteCustomMeasureWarning from "./DeleteCustomMeasureWarning";
import type { MeasureDescriptor } from "../../../../../../../../../api/types/svTypes.ts";

interface Props {
  measure: MeasureDescriptor;
  onCancel: () => void;
}

export const DeleteMeasureDialog = ({ measure, onCancel }: Props) => {
  const dispatch = useDispatch();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [saving, setSaving] = useState(false);
  const getCustomMeasureReportsUsage = useCallback(
    () => biApi.getUsedByReports(measure.measureId ?? measure.name),
    [measure.measureId, measure.name]
  );
  const [reportsUsageResponse, fetchError, { isFetching }] = useFetch(getCustomMeasureReportsUsage);

  const onDeleteMeasure = useCallback(() => {
    setSaving(true);
    biApi
      .deleteCustomMeasure(measure.measureId ?? measure.name)
      .then(() => {
        dispatch(createMetaDataAction("deleteMeasure", measure));
        sendNotification(`The measure "${measure.caption}" has been deleted.`);
        onCancel();
      })
      .catch(() => {
        sendNotificationError("An error occurred while deleting the measure.");
        setSaving(false);
      });
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [measure.name, onCancel]);

  return (
    <Dialog open fullWidth slotProps={{ paper: { sx: { maxWidth: 514, minHeight: 205, height: "auto" } } }}>
      <DialogTitle>Delete</DialogTitle>
      <DialogCloseButton onClick={onCancel} />
      {isFetching && !fetchError && (
        <Box sx={{ px: 3, py: 2.5 }}>
          <InlineLoader />
        </Box>
      )}
      {fetchError && !isFetching && (
        <Box sx={{ px: 3, py: 2.5 }}>
          <Alert severity="error" sx={{ width: "100%" }}>
            Couldn’t get the reports usage information
          </Alert>
        </Box>
      )}
      {!fetchError && !isFetching && (
        <DialogContent>
          <Stack spacing={2} sx={{ alignItems: "center" }}>
            <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
            <Stack spacing={1} sx={{ alignItems: "center" }}>
              <Typography variant="h6" sx={{ textAlign: "center", wordBreak: "break-all" }}>
                Delete {measure.caption} Custom Measure?
              </Typography>
              <Typography color="textSecondary" sx={{ textAlign: "center" }}>
                You’re about to delete this custom measure. This action is permanent and cannot be undone.
              </Typography>
            </Stack>
            {reportsUsageResponse &&
              (reportsUsageResponse.reports.length > 0 || reportsUsageResponse.hiddenReportsCount > 0) && (
                <Stack spacing={2}>
                  <DeleteCustomMeasureWarning />
                  <UsedByReports
                    reports={reportsUsageResponse.reports}
                    hiddenReportsCount={reportsUsageResponse.hiddenReportsCount}
                    minRows={3}
                    maxRows={6}
                  />
                </Stack>
              )}
          </Stack>
        </DialogContent>
      )}
      <DialogButtons onCancel={onCancel}>
        <Button
          variant="outlined"
          color="error"
          disabled={saving || isFetching || !!fetchError}
          loading={saving}
          onClick={onDeleteMeasure}
        >
          Delete
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default DeleteMeasureDialog;
